// Sort Search Block Component
// Handles sorting and filtering functionality for company list

class SortSearchBlock {
  constructor() {
    this.init();
  }

  init() {
    this.bindEvents();
    this.initializeFilterState();
  }

  bindEvents() {
    // Bind sort change event
    document.addEventListener('change', (e) => {
      if (e.target.classList.contains('sort-select')) {
        this.handleSortChange(e.target.value);
      }
    });

    // Bind filter click events
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('btn') && e.target.hasAttribute('data-filter')) {
        const filterType = e.target.getAttribute('data-filter');
        this.handleFilterClick(e.target, filterType);
      }
    });
  }

  validateSortType(sortType) {
    // Check for null/undefined
    if (sortType === null || sortType === undefined) {
      console.warn('Invalid sortType: sortType is null or undefined');
      return { isValid: false, normalizedValue: null };
    }

    // Convert to string and trim whitespace
    const normalizedSortType = String(sortType).trim();

    // Validate against allowed sort types
    const allowedSortTypes = ['', 'latest_reviews', 'review_count', 'rating_desc', 'assessment_results'];
    if (!allowedSortTypes.includes(normalizedSortType)) {
      console.warn(`Invalid sortType: "${normalizedSortType}" is not in allowed list:`, allowedSortTypes);
      return { isValid: false, normalizedValue: null };
    }

    return { isValid: true, normalizedValue: normalizedSortType };
  }

  handleSortChange(sortType) {
    // Validate sortType
    const validation = this.validateSortType(sortType);
    if (!validation.isValid) {
      return;
    }

    const currentUrl = new URL(window.location);

    // Remove sort_type param if "標準" is selected (empty value)
    if (validation.normalizedValue === '') {
      currentUrl.searchParams.delete('sort_type');
    } else {
      currentUrl.searchParams.set('sort_type', validation.normalizedValue);
    }

    window.location.href = currentUrl.toString();
  }

  handleFilterClick(button, filterType) {
    // Toggle active state for the clicked button
    button.classList.toggle('active');

    // Get all active filters
    const activeFilters = [];
    document.querySelectorAll('.form-buttons .btn.active').forEach(btn => {
      activeFilters.push(btn.getAttribute('data-filter'));
    });

    // Save filters to localStorage
    this.saveFiltersToLocalStorage(activeFilters);

    // Filter stores based on active filters (without changing URL)
    this.filterStores(activeFilters);
  }

  filterStores(activeFilters) {
    const storeElements = document.querySelectorAll('.store-table section');

    storeElements.forEach(storeElement => {
      const shouldShow = this.checkKeishikiStatus(storeElement, activeFilters);
      storeElement.style.display = shouldShow ? 'block' : 'none';
    });
  }

  checkKeishikiStatus(storeElement, activeFilters) {
    // If no filters are active, show all stores
    if (activeFilters.length === 0) {
      return true;
    }

    // Check if store supports ALL of the active filters by looking at keishiki elements
    for (const filterType of activeFilters) {
      // Find the keishiki element for this filter type
      const keishikiElements = storeElement.querySelectorAll('.keishiki');
      let foundMatchingKeishiki = false;

      for (const keishikiElement of keishikiElements) {
        if (keishikiElement.textContent.trim() === this.getKeishikiLabel(filterType)) {
          // Check if this keishiki has is_true class (meaning it's supported)
          if (keishikiElement.classList.contains('is_true')) {
            foundMatchingKeishiki = true;
            break;
          }
        }
      }

      // If any filter type is not supported (is_true), return false
      if (!foundMatchingKeishiki) {
        return false;
      }
    }

    return true; // Store supports all active filters
  }

  getKeishikiLabel(filterType) {
    switch(filterType) {
      case 'takuhai':
        return '宅配';
      case 'tenpo':
        return '店頭';
      case 'shuccho':
        return '出張';
      default:
        return '';
    }
  }

  initializeFilterState() {
    // Check if there's a sort_type parameter in URL (for scrolling)
    const urlParams = new URLSearchParams(window.location.search);
    const sortTypeParam = urlParams.get('sort_type');

    // Check if page exists in URL path (e.g., /page/2/)
    const pageInPath = this.getPageFromPath();

    // Clear filters if both sort_type and page are null
    if (sortTypeParam === null && pageInPath === null) {
      this.clearFiltersFromLocalStorage();
    } else {
      // Load and apply filters from localStorage
      this.loadAndApplyFiltersFromLocalStorage();
    }

    // Scroll to sort-search-block if sort_type parameter exists
    if (sortTypeParam) {
      this.scrollToSortSearchBlock();
    }
  }

  scrollToSortSearchBlock() {
    const sortSearchBlock = document.querySelector('.sort-search-block');
    if (sortSearchBlock) {
      // Add a small delay to ensure page is fully loaded
      setTimeout(() => {
        sortSearchBlock.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }
  }

  saveFiltersToLocalStorage(activeFilters) {
    try {
      localStorage.setItem('hikakaku_active_filters', JSON.stringify(activeFilters));
    } catch (e) {
      console.warn('Could not save filters to localStorage:', e);
    }
  }

  loadFiltersFromLocalStorage() {
    try {
      const savedFilters = localStorage.getItem('hikakaku_active_filters');
      return savedFilters ? JSON.parse(savedFilters) : [];
    } catch (e) {
      console.warn('Could not load filters from localStorage:', e);
      return [];
    }
  }

  loadAndApplyFiltersFromLocalStorage() {
    const activeFilters = this.loadFiltersFromLocalStorage();

    if (activeFilters.length > 0) {
      // Activate corresponding buttons
      activeFilters.forEach(filterType => {
        const button = document.querySelector(`[data-filter="${filterType}"]`);
        if (button) {
          button.classList.add('active');
        }
      });

      // Apply filters
      this.filterStores(activeFilters);
    }
  }

  clearFiltersFromLocalStorage() {
    try {
      localStorage.removeItem('hikakaku_active_filters');
    } catch (e) {
      console.warn('Could not clear filters from localStorage:', e);
    }
  }

  getPageFromPath() {
    // Simply check if '/page/' exists in the URL path
    return window.location.pathname.includes('/page/') ? 'exists' : null;
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new SortSearchBlock();
});

// Export for potential use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = SortSearchBlock;
}
